/**
 * Вкладка «Справочник услуг» раздела «Экология» панели администратора (ecology-catalog, макет
 * 16.09.2026). Таблица позиций: название (с пометками «скрыт из заказа» / «без строки в пульте»),
 * тип, срок действия / периодичность, срок разработки, согласование, категории; поиск по названию
 * без перезагрузки; добавление и правка — модалкой `CatalogItemDialog`. Удалить можно только позицию,
 * добавленную администратором (с подтверждением); встроенную — нельзя, на неё опираются опрос и пульт.
 */
import { useMemo, useState } from 'react'
import { ClipboardList, Leaf, ListChecks, Pencil, Plus, Trash2 } from 'lucide-react'
import { adminApi, type EcoCatalogItem, type EcoQuestion } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { ECO_KIND_SHORT_LABELS, ECO_SECTION_LABELS, ecoTermLabel } from '@/lib/ecology'
import { Panel } from '@/design-system/components/layout/panel'
import { Toolbar } from '@/design-system/components/layout/toolbar'
import { Button } from '@/design-system/components/primitives/button'
import { Input } from '@/design-system/components/primitives/input'
import { ConfirmDialog } from '@/design-system/components/primitives/confirm-dialog'
import { Alert } from '@/design-system/components/feedback/alert'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'
import { CatalogItemDialog } from './catalog-item-dialog'
import { CardFieldsDialog } from './card-fields-dialog'
import { OrderFieldsDialog } from './order-fields-dialog'
import { CategoryChips } from './category-chips'

/** Состояние модалки позиции: закрыта / новая позиция / правка. */
type DialogState = { open: false } | { open: true; item: EcoCatalogItem | null }

function dash(value: string): string {
  return value.trim() ? value : '—'
}

export function CatalogTab({
  items,
  questions,
  onItemsChange,
}: {
  items: EcoCatalogItem[]
  /** Вопросы опросника — для «Вопросов при заказе» в модалке позиции (ecology-order). */
  questions: EcoQuestion[]
  onItemsChange: (items: EcoCatalogItem[]) => void
}) {
  const [search, setSearch] = useState('')
  const [dialog, setDialog] = useState<DialogState>({ open: false })
  const [confirmDelete, setConfirmDelete] = useState<EcoCatalogItem | null>(null)
  // Позиция, у которой настраиваются поля карточки строки (ecology-card-fields-admin).
  const [cardItem, setCardItem] = useState<EcoCatalogItem | null>(null)
  const [orderFieldsItem, setOrderFieldsItem] = useState<EcoCatalogItem | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return q ? items.filter((i) => i.name.toLowerCase().includes(q)) : items
  }, [items, search])

  function onSaved(saved: EcoCatalogItem) {
    const exists = items.some((i) => i.id === saved.id)
    onItemsChange(exists ? items.map((i) => (i.id === saved.id ? saved : i)) : [...items, saved])
    setDialog({ open: false })
  }

  async function remove() {
    if (!confirmDelete) return
    setActionError(null)
    setDeleting(true)
    try {
      await adminApi.ecology.deleteItem(confirmDelete.id)
      onItemsChange(items.filter((i) => i.id !== confirmDelete.id))
      setConfirmDelete(null)
    } catch (err) {
      setConfirmDelete(null)
      setActionError(getErrorMessage(err, 'Не удалось удалить позицию справочника'))
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div>
      <Toolbar aria-label="Поиск и добавление позиций справочника">
        <div className="min-w-56 flex-1">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск по названию"
            aria-label="Поиск по названию"
          />
        </div>
        <Button
          className="max-w-full whitespace-normal"
          onClick={() => setDialog({ open: true, item: null })}
          leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
        >
          Добавить услугу или документ
        </Button>
      </Toolbar>
      <p className="mb-3 text-12 text-txt-tertiary">
        Из этого списка клиент выбирает услугу при заказе, а кабинет берёт сроки действия для
        светофора. Категории — кому документ обязателен; условия — на вкладке «Кому что нужно».
      </p>

      {actionError ? (
        <Alert variant="danger" className="mb-4">
          {actionError}
        </Alert>
      ) : null}

      <Panel>
        {filtered.length === 0 ? (
          <EmptyState
            icon={Leaf}
            title={items.length === 0 ? 'Справочник пуст' : 'Ничего не найдено'}
            description={
              items.length === 0
                ? 'Добавьте первую услугу или документ'
                : 'Измените запрос — поиск идёт по названию'
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <DataTable>
              <DataTableHead>
                <tr>
                  <DataTableHeaderCell>Услуга / документ</DataTableHeaderCell>
                  <DataTableHeaderCell>Тип</DataTableHeaderCell>
                  <DataTableHeaderCell>Срок действия / периодичность</DataTableHeaderCell>
                  <DataTableHeaderCell>Срок разработки</DataTableHeaderCell>
                  <DataTableHeaderCell>Согласование</DataTableHeaderCell>
                  <DataTableHeaderCell>Категории</DataTableHeaderCell>
                  <DataTableHeaderCell>{''}</DataTableHeaderCell>
                </tr>
              </DataTableHead>
              <DataTableBody>
                {filtered.map((item) => {
                  const notes = [
                    item.trainingLinked
                      ? 'по журналу обучения · заказ через «Обучение»'
                      : item.showInOrder
                        ? null
                        : 'скрыт из заказа',
                    item.tracked ? null : 'без строки в пульте',
                  ].filter(Boolean)
                  return (
                    <DataTableRow key={item.id}>
                      <DataTableCell className="font-medium text-txt">
                        {item.name}
                        {notes.length > 0 ? (
                          <p className="mt-0.5 text-12 font-normal text-txt-tertiary">
                            {notes.join(' · ')}
                          </p>
                        ) : null}
                      </DataTableCell>
                      <DataTableCell className="text-txt-secondary">
                        {ECO_KIND_SHORT_LABELS[item.kind]}
                        {item.section === 'lab' ? (
                          <span className="block text-12 text-txt-tertiary">
                            {ECO_SECTION_LABELS.lab.toLowerCase()}
                          </span>
                        ) : null}
                      </DataTableCell>
                      <DataTableCell className="text-txt-secondary">
                        {ecoTermLabel(item)}
                      </DataTableCell>
                      <DataTableCell className="text-txt-secondary">
                        {dash(item.developmentTerm)}
                      </DataTableCell>
                      <DataTableCell className="text-txt-secondary">
                        {dash(item.approval)}
                      </DataTableCell>
                      <DataTableCell>
                        <CategoryChips categories={item.categories} />
                      </DataTableCell>
                      <DataTableCell className="text-right">
                        <div className="flex justify-end gap-1.5">
                          {item.tracked && !item.trainingLinked ? (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => setCardItem(item)}
                              aria-label={`Поля карточки «${item.name}»`}
                              title="Поля карточки"
                            >
                              <ListChecks className="size-4 stroke-current" aria-hidden />
                            </Button>
                          ) : null}
                          {item.showInOrder && !item.trainingLinked ? (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => setOrderFieldsItem(item)}
                              aria-label={`Поля заявки «${item.name}»`}
                              title="Поля заявки"
                            >
                              <ClipboardList className="size-4 stroke-current" aria-hidden />
                            </Button>
                          ) : null}
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setDialog({ open: true, item })}
                            aria-label={`Изменить «${item.name}»`}
                            title="Изменить"
                          >
                            <Pencil className="size-4 stroke-current" aria-hidden />
                          </Button>
                          {item.builtin ? null : (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => setConfirmDelete(item)}
                              aria-label={`Удалить «${item.name}»`}
                              title="Удалить"
                            >
                              <Trash2 className="size-4 stroke-current" aria-hidden />
                            </Button>
                          )}
                        </div>
                      </DataTableCell>
                    </DataTableRow>
                  )
                })}
              </DataTableBody>
            </DataTable>
          </div>
        )}
      </Panel>
      <p className="mt-2 text-12 text-txt-tertiary">Услуг и документов: {filtered.length}</p>

      <CatalogItemDialog
        open={dialog.open}
        item={dialog.open ? dialog.item : null}
        items={items}
        questions={questions}
        onClose={() => setDialog({ open: false })}
        onSaved={onSaved}
      />

      <CardFieldsDialog
        open={cardItem !== null}
        item={cardItem}
        onClose={() => setCardItem(null)}
        onSaved={(saved) => {
          onItemsChange(items.map((i) => (i.id === saved.id ? saved : i)))
          setCardItem(null)
        }}
      />

      <OrderFieldsDialog
        open={orderFieldsItem !== null}
        item={orderFieldsItem}
        onClose={() => setOrderFieldsItem(null)}
        onSaved={(saved) => {
          onItemsChange(items.map((i) => (i.id === saved.id ? saved : i)))
          setOrderFieldsItem(null)
        }}
      />

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Удаление из справочника"
        message={
          confirmDelete
            ? `Удалить «${confirmDelete.name}» из справочника? У клиентов строки с этим документом останутся, новые заказы по нему станут недоступны.`
            : ''
        }
        onClose={() => setConfirmDelete(null)}
        closeDisabled={deleting}
        buttons={[
          {
            label: deleting ? 'Удаление…' : 'Удалить',
            variant: 'danger',
            disabled: deleting,
            onClick: () => void remove(),
          },
          {
            label: 'Отмена',
            variant: 'secondary',
            disabled: deleting,
            onClick: () => setConfirmDelete(null),
          },
        ]}
      />
    </div>
  )
}
